import { LanguagesEnum } from '@/api/enums';
import { ListingResponse } from '@/api/types';
import { InteractiveElement } from '@/client/courses';
import { File } from '@/client/files';
import { SurveyCategoryEnum } from '@/client/security-culture/types';
import { GenericForbiddenApiError } from '@/hooks/query/types';
import { UseMutationResult } from 'react-query';
import { AnswerOptionType } from '../enums';

export type ImageSettings = {
  withBorder?: boolean;
  withZoom?: boolean;
};

export type ContentTranslation = {
  language: LanguagesEnum;
  text?: string;
  file?: File | null;
  explanation?: string;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  placeholderText?: string;
  tooltipText?: string;
  imageSettings?: ImageSettings;
  interactiveElement?: InteractiveElement;
};

export type ModifiedContentTranslations = {
  [language: string]: {
    text?: string;
    file?: File | null;
    explanation?: string;
    scaleMinLabel?: string;
    scaleMaxLabel?: string;
    placeholderText?: string;
    tooltipText?: string;
    imageSettings?: ImageSettings;
    interactiveElement?: InteractiveElement;
  };
};

export type AnswerOption = {
  id?: string;
  defaultText?: string;
  position: number;
  isCorrect?: boolean;
  hidden?: boolean;
  type: AnswerOptionType;
  translations: ContentTranslation[];
};

export type ModifiedAnswerOption = {
  id?: string;
  position: number;
  isCorrect?: boolean;
  hidden?: boolean;
  type: AnswerOptionType;
  translations: ModifiedContentTranslations;
};

export type Question = {
  id?: string;
  position?: number;
  scaleMin?: number;
  scaleMax?: number;
  translations: ContentTranslation[];
  answerOptions?: AnswerOption[];
  type: QuestionType;
  questionId?: string;
  label?: SurveyCategoryEnum;
  isAiCustomizationEnabled?: boolean;
  aiCustomizationInstructions?: string;
};

export enum QuestionType {
  SINGLE_CHOICE = 'single-choice',
  MULTIPLE_CHOICE = 'multiple-choice',
  RATING_SCALE = 'rating-scale',
  RATING_SCALE_WITH_TEXT = 'rating-scale-with-text',
  TEXT = 'text',
}

export type ModifiedQuestion = {
  id?: string;
  position?: number;
  scaleMin?: number;
  scaleMinLabel?: string;
  scaleMax?: number;
  scaleMaxLabel?: string;
  translations: ModifiedContentTranslations;
  answerOptions: ModifiedAnswerOption[];
  type?: QuestionType;
  label?: SurveyCategoryEnum;
  imageSettings?: ImageSettings;
  isAiCustomizationEnabled?: boolean;
  aiCustomizationInstructions?: string;
};

export type Quiz = {
  id?: string;
  title: string;
  supportedLanguages?: LanguagesEnum[];
  fullyTranslatedLanguages?: LanguagesEnum[];
  questions: Question[];
  type: QuizType;
  account: string | null;
  logo?: string;
};

export enum QuizType {
  TRADITIONAL = 'traditional',
  SURVEY = 'survey',
  FEEDBACK = 'feedback',
}

export type ModifiedQuiz = Quiz & {
  questions: ModifiedQuestion[];
};

export type QuizAnswerResponse = {
  quizAttemptId: string;
  isCorrect?: boolean;
  correctAnswerIds: string[];
  explanation?: string;
  score?: number;
  courseAttempt?: string;
};

export type SurveySubmission = {
  answers: SurveyAnswer[];
};

export type SurveyAnswer = {
  questionId: string;
  answer: number | string;
};

export const quizSupportedLanguages = [
  LanguagesEnum.ZH,
  LanguagesEnum.CZ,
  LanguagesEnum.DK,
  LanguagesEnum.NL,
  LanguagesEnum.EN,
  LanguagesEnum.FI,
  LanguagesEnum.FR,
  LanguagesEnum.DE,
  LanguagesEnum.KL,
  LanguagesEnum.IT,
  LanguagesEnum.LV,
  LanguagesEnum.LT,
  LanguagesEnum.NO,
  LanguagesEnum.PL,
  LanguagesEnum.PG,
  LanguagesEnum.ES,
  LanguagesEnum.SE,
  LanguagesEnum.TR,
  LanguagesEnum.VI,
  LanguagesEnum.AR,
  LanguagesEnum.UA,
  LanguagesEnum.RO,
];

export type CloneQuizRequest = {
  id: string;
  account: string;
};

export type DeleteQuizRequest = {
  accountId: string;
  quizId: string;
};

export type CloneQuizMutation = UseMutationResult<
  Quiz,
  GenericForbiddenApiError,
  CloneQuizRequest
>;

export type DeleteQuizMutation = UseMutationResult<
  boolean,
  GenericForbiddenApiError,
  string
>;

export type ListQuizzesResponse = ListingResponse<Quiz>;

export type FeedbackAnswer = {
  scaleResponse: number;
  textResponse?: string;
};

export type QuizAnswerStatistics = {
  questionId: string;
  correctFirstAttempts: string | null;
  correctSubsequentAttempts: string | null;
  questionLabel: string;
  totalEnrolledUsers: string;
  totalSubsequentUsers: string;
};
